import React, { useMemo } from 'react';
import _ from 'lodash';

import { matchPath } from 'react-router-dom';
import { Flex, Sidebar as ReactSideBar, Nav, NavGroup } from '@patternfly/react-core';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useIsAuthenticated } from '@azure/msal-react';

import { useTabletOrMobile } from '../../utils/hooks/useTabletOrMobile';
import { MenuItemPropType, SideBarItem, SideBarItemType } from './SideBarItems';
import { useTranslation } from '../../localization/useTranslation';
import styles from './SideBar.module.css';

const MenuItem = ({ menuItem, isSideBarCollapsed, toggleSideBar }) => {
  const location = useLocation();

  const menuPath = _.isArray(menuItem.path) ? _.head(menuItem.path) : menuItem.path;
  const path = _.isObject(menuPath) ? menuPath.path : menuPath;
  const isActive = path ?
    matchPath(location.pathname, { path, exact: false, strict: false }) != null :
    false;

  return (<SideBarItem
    menuItem={menuItem}
    isActive={isActive}
    isSideBarCollapsed={isSideBarCollapsed}
    toggleSideBar={toggleSideBar} />);
};

const MenuGroup = ({ menuGroup, isSideBarCollapsed, toggleSideBar }) => {
  const T = useTranslation();
  const emptyName = menuGroup.name ? 'A' : '';
  return (
    <NavGroup className={isSideBarCollapsed && styles.noGroupText} aria-label={'Group Heading'} title={isSideBarCollapsed ? emptyName : T.localize(menuGroup.name)}>{
      _.map(menuGroup.items, (menuItem, menuIndex) => {
        return (<MenuItem
          key={menuIndex}
          menuItem={menuItem}
          toggleSideBar={toggleSideBar}
          isSideBarCollapsed={isSideBarCollapsed} />);
      })
    }
    </NavGroup>);
};

// Create groups from the menu, at each heading the new group starts
const createGroupedMenu = (menu) => {
  return _.reduce(menu, (result, menuItem) => {
    if (menuItem.type === SideBarItemType.HEADING) {
      // New group when we get a heading
      return [...result, { name: menuItem.name, items: [] }];
    }
    if (result.length === 0) {
      // If first item is not a heading, then create an empty group
      return [...result, { name: '', items: [menuItem] }];
    }
    // Standard item, add to current heading/group
    result[result.length - 1].items.push(menuItem);
    return result;
  }, []);
};

export const SideBar = ({ isSideBarCollapsed, toggleSideBar, menu }) => {
  const isTabletOrMobile = useTabletOrMobile();
  const isAuthenticated = useIsAuthenticated();

  // Hide sidebar when collapsed in mobile mode
  const showSideBar = !(isTabletOrMobile && isSideBarCollapsed);

  const activeMenu = useMemo(() => {
    return createGroupedMenu(_.filter(menu, menuItem => {
      return (menuItem.name != null || menuItem.type != null) && (isAuthenticated || menuItem.visibleWhenLoggedOut);
    }));
  }, [isAuthenticated, menu]);

  return (
    <ReactSideBar
      aria-label="Navigation bar"
      className={classNames('main-sidebar',
        !showSideBar && styles.sidebarNone,
        showSideBar && styles.sidebarBlock)}
      style={{ zIndex: 400 }}
    >
      <Flex
        className={classNames(styles.sidebarWrapper,
          !isSideBarCollapsed && !isTabletOrMobile && styles.sidebarDesktopWidth,
          isSideBarCollapsed && styles.sidebarWrapperIconWidth,
          isTabletOrMobile && styles.sidebarWrapperMobile)}
      >
        <Nav className={classNames(styles.sidebarList,
          isTabletOrMobile && styles.sidebarTabletFontsize,
          !isTabletOrMobile && styles.sidebarDesktopFontsize,
          isTabletOrMobile && styles.sidebarListFullWidth)} >
          { _.map(activeMenu, (menuGroup, menuGroupIndex) =>
            (<MenuGroup
              className={classNames(styles.sidebarWrapper,
                isTabletOrMobile && styles.sidebarTabletFontsize,
                !isTabletOrMobile && styles.sidebarDesktopFontsize,)}
              key={menuGroupIndex}
              menuGroup={menuGroup}
              isSideBarCollapsed={isSideBarCollapsed}
              toggleSideBar={toggleSideBar}/>))
          }
        </Nav>
      </Flex>
    </ReactSideBar>
  );
};

SideBar.propTypes = {
  isSideBarCollapsed: PropTypes.bool,
  toggleSideBar: PropTypes.func,
  menu: PropTypes.arrayOf(MenuItemPropType)
};
